import { RegisteredModel } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_pb';
import { GetRegisteredModelRequest } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_service_pb';
import { RegisteredModelService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_service_connect';
import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { IBreadcrumbItem, MessageBarType } from '@fluentui/react';
import { useHaicPageTitle, useToast } from '@h2oai/ui-kit';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { HeaderPure } from '../../components/Header/Header';
import { GrpcStatusCode, isGrpcGatewayErrorResponse } from '../../services/HTTPResponse';
import { useCloudPlatformDiscovery, useEnv } from '../../utils/hooks';
import { formatError } from '../../utils/utils';
import { RoutePaths } from '../Routes';
import { WORKSPACE_DEPLOYMENT_ID_SEPARATOR } from './constants';
import { ENDPOINTS } from './constants';
import { MLOpsError404Page } from './DeploymentDetail';
import ModelDetailContent from './ModelDetailContent';
import NavigationWrapper from './NavigationWrapper';
import PageWrapper from './PageWrapper';

type AdminModelDetailParams = { workspace_model_id: string; action?: string };

export const AdminModelDetail = () => {
  const history = useHistory();
  const env = useEnv();
  const params = useParams<AdminModelDetailParams>();
  const { addToast } = useToast();

  const [workspaceId, modelId] = params.workspace_model_id.split(WORKSPACE_DEPLOYMENT_ID_SEPARATOR, 2);
  const { action } = params;

  const isNew = React.useMemo(() => modelId === 'add-new', [modelId]);
  const isNewVersionUpload = React.useMemo(() => action === 'add-new-version', [action]);

  const [model, setModel] = React.useState<RegisteredModel | null>(null);
  const [loadingModel, setLoadingModel] = React.useState(!isNew);
  const [showNotFound, setShowNotFound] = React.useState(false);

  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const storageTransport = React.useMemo(
    () =>
      createConnectTransport({
        baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
      }),
    [mlopsApiUrl]
  );
  const registeredModelClient = React.useMemo(
    () => createClient(RegisteredModelService, storageTransport),
    [storageTransport]
  );

  const getRegisteredModel = React.useCallback(
    async (id: string) => {
      setLoadingModel(true);
      try {
        const response = await registeredModelClient.getRegisteredModel(new GetRegisteredModelRequest({ modelId: id }));
        const modelData = response?.registeredModel;
        if (modelData) {
          setModel(modelData);
          setShowNotFound(false);
        }
      } catch (err) {
        if (
          isGrpcGatewayErrorResponse(err) &&
          (err.code === GrpcStatusCode.NOT_FOUND ||
            err.code === GrpcStatusCode.INVALID_ARGUMENT ||
            err.code === GrpcStatusCode.INTERNAL)
        ) {
          setShowNotFound(true);
        } else {
          const message = `Failed to fetch model data: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        }
      } finally {
        setLoadingModel(false);
      }
    },
    [addToast, registeredModelClient]
  );

  React.useEffect(() => {
    if (modelId && !isNew) {
      void getRegisteredModel(modelId);
    }
  }, [modelId, isNew, getRegisteredModel]);

  const breadcrumbs: IBreadcrumbItem[] = React.useMemo(
    () => [
      {
        key: `home`,
        text: `home`,
        onClick: () => history.push('/'),
      },
      {
        key: `admin-models`,
        text: `Admin Models`,
        onClick: () => history.push(RoutePaths.MLOPS_ADMIN_MODELS),
      },
      {
        key: 'model',
        text: isNewVersionUpload ? 'Add new model version' : model?.displayName || modelId || 'Model details',
        isCurrentItem: true,
      },
    ],
    [history, model, modelId, isNewVersionUpload]
  );

  useHaicPageTitle(`${model?.displayName || 'Model Detail'} | MLOps Admin`, env?.cloudInstanceName);

  if (showNotFound) {
    return MLOpsError404Page(RoutePaths.MLOPS_ADMIN_MODELS, 'Return to all models');
  }

  return (
    <NavigationWrapper>
      <PageWrapper>
        <HeaderPure
          items={breadcrumbs}
          pageTitle={isNewVersionUpload ? 'Add new model version' : model?.displayName || 'Model details'}
        />
        <ModelDetailContent
          model={model}
          loadingModel={loadingModel}
          workspaceId={workspaceId}
          modelId={modelId}
          action={action}
          isNew={isNew}
          isNewVersionUpload={isNewVersionUpload}
          workspaceFQDN={`workspaces/${workspaceId}`} // Admin pages might not have FQDN readily, construct it.
        />
      </PageWrapper>
    </NavigationWrapper>
  );
};

export default AdminModelDetail;
